// ---------------------------------------------------------------------------
// Test Mode — simulated tasks for Clean Code Culture SRL (CLE-250 / CLE-255).
//
// Tasks are generated deterministically PER PROJECT (12 each) so every project
// — including the 6 added in CLE-255 — owns 10-20 tasks with a realistic mix of
// statuses, priorities, labels and assignees drawn from its own team. The
// per-project status cycle keeps a healthy spread across backlog / todo /
// in_progress / in_review / done (+ the occasional cancelled).
// ---------------------------------------------------------------------------

import type { Task, TaskPriority, Label } from '../lib/tasksApi';
import { isoDate, isoDateTime } from './company';
import { PROJECT_SEEDS } from './projects';
import { PEOPLE_REFS } from './people';
import { TEST_STATUS_BY_SLUG } from './taskStatuses';

export const TEST_LABELS: Label[] = [
  { id: 'lbl-bug', name: 'bug', color: '#ef4444' },
  { id: 'lbl-feature', name: 'feature', color: '#3b82f6' },
  { id: 'lbl-improvement', name: 'improvement', color: '#22c55e' },
  { id: 'lbl-tech-debt', name: 'tech-debt', color: '#a855f7' },
  { id: 'lbl-hotfix', name: 'hotfix', color: '#f97316' },
];

const PRIORITY_VALUE: Record<TaskPriority, number> = {
  NONE: 0,
  URGENT: 1,
  HIGH: 2,
  MEDIUM: 3,
  LOW: 4,
};

// Per-project status cycle (length 12) — one task per slot gives every project
// 12 tasks spread across all statuses (4 done, 2 in_progress, 1 in_review,
// 2 todo, 2 backlog, 1 cancelled). Values are configurable-status slugs.
const STATUS_CYCLE: string[] = [
  'done',
  'done',
  'in_progress',
  'todo',
  'backlog',
  'done',
  'in_review',
  'in_progress',
  'todo',
  'done',
  'backlog',
  'cancelled',
];

// Per-project priority cycle (length 12) — varied, weighted toward MEDIUM.
const PRIORITY_CYCLE: TaskPriority[] = [
  'HIGH',
  'MEDIUM',
  'URGENT',
  'MEDIUM',
  'LOW',
  'HIGH',
  'MEDIUM',
  'MEDIUM',
  'LOW',
  'NONE',
  'MEDIUM',
  'LOW',
];

// Title fragments to give tasks realistic, varied names.
const VERBS = [
  'Implementează',
  'Refactorizează',
  'Repară',
  'Adaugă',
  'Optimizează',
  'Testează',
  'Documentează',
  'Integrează',
  'Migrează',
  'Actualizează',
];
const NOUNS = [
  'fluxul de autentificare',
  'pagina de checkout',
  'API-ul de plăți',
  'dashboard-ul de rapoarte',
  'componenta de tabel',
  'sistemul de notificări',
  'cache-ul Redis',
  'pipeline-ul CI',
  'modulul de export',
  'integrarea cu SmartBill',
  'validarea formularelor',
  'layout-ul responsive',
  'middleware-ul de logging',
  'schema bazei de date',
  'componenta de search',
];

// Active projects that should own tasks (skip pure lead/planning placeholders).
const TASK_PROJECTS = PROJECT_SEEDS.filter(
  (p) => p.memberIds.length > 0 || p.status === 'ALMOST_DONE' || p.status === 'ACTIVE',
);

// Tasks generated per project (12 → comfortably within the 10-20 spec).
const TASKS_PER_PROJECT = STATUS_CYCLE.length;

let cached: Task[] | null = null;

export function buildTasks(): Task[] {
  if (cached) return cached;
  const tasks: Task[] = [];
  let seq = 0;

  TASK_PROJECTS.forEach((project, pIdx) => {
    const memberPool = project.memberIds.length ? project.memberIds : PEOPLE_REFS.map((p) => p.id);
    for (let j = 0; j < TASKS_PER_PROJECT; j++) {
      seq += 1;
      // Offset the cycles per project so the board isn't visually identical.
      const statusDef = TEST_STATUS_BY_SLUG[STATUS_CYCLE[(j + pIdx) % STATUS_CYCLE.length]];
      const priority = PRIORITY_CYCLE[(j + pIdx * 2) % PRIORITY_CYCLE.length];
      const assigneeId = memberPool[j % memberPool.length];
      const assignee = PEOPLE_REFS.find((p) => p.id === assigneeId) ?? null;

      const verb = VERBS[(j + pIdx) % VERBS.length];
      const noun = NOUNS[(j * 3 + pIdx) % NOUNS.length];
      const estimate = [2, 4, 8, 16, 24, 40][(j + pIdx) % 6];

      // Due date: closed tasks in the past, open ones from a few days ago to
      // six weeks out. The stride of 7 is coprime with both ranges, so 180
      // tasks land on every single day — the calendar month grid has a task
      // for every person on every day instead of a handful of busy days.
      const closed = statusDef.category === 'COMPLETED' || statusDef.category === 'CANCELLED';
      let dueOffset: number;
      if (closed) dueOffset = -(1 + ((seq * 7) % 40));
      else dueOffset = ((seq * 7) % 45) - 4;
      // A closed task was last touched when it was closed: a day before its
      // deadline for most, two days after for every seventh — so the reports'
      // "finished on time" reads like a healthy team, not a hopeless one.
      const updatedOffset = closed
        ? Math.min(0, dueOffset - (seq % 7 === 0 ? -2 : 1))
        : -((j + pIdx) % 10);

      const labelCount = j % 3 === 0 ? 2 : 1;
      const labels: Label[] = [];
      for (let l = 0; l < labelCount; l++) {
        labels.push(TEST_LABELS[(j + l + pIdx) % TEST_LABELS.length]);
      }

      tasks.push({
        id: `t${seq}`,
        identifier: `${project.code}-${j + 1}`,
        title: `${verb} ${noun}`,
        description: `Sarcină simulată pentru ${project.name}.`,
        status: statusDef.slug,
        statusId: statusDef.id,
        statusLabel: statusDef.name,
        statusColor: statusDef.color,
        statusCategory: statusDef.category,
        statusOrder: statusDef.order,
        departmentId: null,
        department: null,
        serviceId: null,
        service: null,
        priority,
        priorityValue: PRIORITY_VALUE[priority],
        estimate,
        spentHours: null,
        dueDate: isoDate(dueOffset),
        projectId: project.id,
        projectName: project.name,
        assigneeId,
        assignee: assignee ? { id: assignee.id, name: assignee.name } : null,
        parentId: null,
        labels,
        subIssueCount: 0,
        subIssueDoneCount: 0,
        commentCount: (j + pIdx) % 4,
        createdAt: isoDateTime(-(20 + ((j + pIdx) % 60)), 9),
        updatedAt: isoDateTime(updatedOffset, 14),
      });
    }
  });

  cached = tasks;
  return tasks;
}

export function tasksForProject(projectId: string): Task[] {
  return buildTasks().filter((t) => t.projectId === projectId);
}

// --- Personal tasks ("Sarcini personale") ----------------------------------
//
// The demo user's private queue. On the real backend this is a hidden project
// that /api/tasks never lists unless asked for by id, and the same holds here:
// `buildTasks()` does not include these, so boards, reports and the calendar
// stay company-wide, while the personal page — which asks for this project —
// gets only its own eight.

export const PERSONAL_PROJECT = { id: 'proj-personal', code: 'PERS', name: 'Sarcini personale' };
// The company's "Fără proiect" container (tasks without a project).
export const GENERAL_PROJECT = { id: 'proj-general', code: 'GEN', name: 'Fără proiect' };
export const DEMO_USER_REF = { id: 'demo-user', name: 'Demo User' };

const PERSONAL_SEEDS: Array<{
  title: string;
  status: string;
  priority: TaskPriority;
  due: number;
  estimate: number;
}> = [
  {
    title: 'Pregătește raportul lunar pentru board',
    status: 'in_progress',
    priority: 'HIGH',
    due: 1,
    estimate: 4,
  },
  {
    title: 'Revizuiește contractul cu FinTech Partners',
    status: 'todo',
    priority: 'URGENT',
    due: 0,
    estimate: 2,
  },
  {
    title: 'Programează 1:1 cu echipa de QA',
    status: 'todo',
    priority: 'MEDIUM',
    due: 2,
    estimate: 1,
  },
  {
    title: 'Răspunde la oferta IndustriaPlus',
    status: 'in_progress',
    priority: 'HIGH',
    due: -1,
    estimate: 2,
  },
  {
    title: 'Actualizează CV-ul companiei pentru licitație',
    status: 'backlog',
    priority: 'LOW',
    due: 7,
    estimate: 3,
  },
  { title: 'Plătește taxele trimestriale', status: 'todo', priority: 'HIGH', due: 5, estimate: 1 },
  {
    title: 'Citește raportul de securitate AWS',
    status: 'backlog',
    priority: 'LOW',
    due: 12,
    estimate: 2,
  },
  {
    title: 'Trimite feedback pentru noul site',
    status: 'done',
    priority: 'MEDIUM',
    due: -3,
    estimate: 1,
  },
];

let personalCached: Task[] | null = null;

export function buildPersonalTasks(): Task[] {
  if (personalCached) return personalCached;
  personalCached = PERSONAL_SEEDS.map((seed, i) => {
    const statusDef = TEST_STATUS_BY_SLUG[seed.status] ?? TEST_STATUS_BY_SLUG[STATUS_CYCLE[0]!]!;
    return {
      id: `tp${i + 1}`,
      identifier: `${PERSONAL_PROJECT.code}-${i + 1}`,
      title: seed.title,
      description: null,
      status: statusDef.slug,
      statusId: statusDef.id,
      statusLabel: statusDef.name,
      statusColor: statusDef.color,
      statusCategory: statusDef.category,
      statusOrder: statusDef.order,
      departmentId: null,
      department: null,
      serviceId: null,
      service: null,
      priority: seed.priority,
      priorityValue: PRIORITY_VALUE[seed.priority],
      estimate: seed.estimate,
      spentHours: null,
      dueDate: isoDate(seed.due),
      projectId: PERSONAL_PROJECT.id,
      projectName: PERSONAL_PROJECT.name,
      assigneeId: DEMO_USER_REF.id,
      assignee: { ...DEMO_USER_REF },
      parentId: null,
      labels: i % 3 === 0 ? [TEST_LABELS[i % TEST_LABELS.length]!] : [],
      subIssueCount: 0,
      subIssueDoneCount: 0,
      commentCount: 0,
      createdAt: isoDateTime(-(10 + i), 9),
      updatedAt: isoDateTime(-(i % 4), 15),
    };
  });
  return personalCached;
}

/** A task by id from either list — the detail page does not know which. */
export function findTask(id: string): Task | undefined {
  return buildTasks().find((t) => t.id === id) ?? buildPersonalTasks().find((t) => t.id === id);
}

// --- Task detail builders (demo audit) ------------------------------------

export function buildTaskTimeEntries(taskId: string) {
  const task = buildTasks().find((t) => t.id === taskId);
  const assignee = task?.assignee ?? { id: 'p7', name: 'Alexandru Ionescu' };
  const entries = [
    {
      id: `te-${taskId}-1`,
      taskId,
      userId: assignee.id,
      userName: assignee.name,
      hours: 3.5,
      date: isoDate(-3),
      note: 'Implementare logică principală',
    },
    {
      id: `te-${taskId}-2`,
      taskId,
      userId: assignee.id,
      userName: assignee.name,
      hours: 2.0,
      date: isoDate(-2),
      note: 'Teste unitare și integrare',
    },
    {
      id: `te-${taskId}-3`,
      taskId,
      userId: assignee.id,
      userName: assignee.name,
      hours: 1.5,
      date: isoDate(-1),
      note: 'Code review și ajustări',
    },
  ];
  return { entries, totalHours: entries.reduce((s, e) => s + e.hours, 0) };
}

/**
 * Demo comments and change history for a task, in the exact shape the API
 * serializes.
 *
 * These used to be invented shapes (`body`, `authorName`, `type`,
 * `description`) that no component could read, so the demo account's task page
 * showed an empty discussion and an empty history. They now mirror
 * `TaskComment` and `TaskActivity` from lib/tasksApi.
 */
export function buildTaskComments(taskId: string) {
  const task = buildTasks().find((t) => t.id === taskId);
  const people = PEOPLE_REFS;
  const author = (p: { id: string; name: string }) => ({ id: p.id, name: p.name });
  const assignee = task?.assignee ?? people[1];

  const comments = [
    {
      id: `comment-${taskId}-1`,
      taskId,
      authorId: people[0].id,
      author: author(people[0]),
      content: 'Am început lucrul la acest task. Estimez finalizare în 2-3 zile.',
      attachments: [],
      createdAt: isoDateTime(-5, 10),
      updatedAt: isoDateTime(-5, 10),
    },
    {
      id: `comment-${taskId}-2`,
      taskId,
      authorId: assignee.id,
      author: author(assignee),
      content:
        'Progres bun — am implementat funcționalitatea de bază. Rămâne de integrat cu modulul existent.',
      attachments: [],
      createdAt: isoDateTime(-3, 14),
      updatedAt: isoDateTime(-3, 14),
    },
    {
      id: `comment-${taskId}-3`,
      taskId,
      authorId: people[2].id,
      author: author(people[2]),
      content: 'Am făcut code review. Câteva sugestii minore de refactorizare, altfel arată bine.',
      attachments: [],
      createdAt: isoDateTime(-1, 16),
      updatedAt: isoDateTime(-1, 16),
    },
  ];
  return { comments };
}

export function buildTaskActivity(taskId: string) {
  const task = buildTasks().find((t) => t.id === taskId);
  const people = PEOPLE_REFS;
  const assignee = task?.assignee ?? people[1];
  const reporter = people[0];

  const row = (
    n: number,
    at: string,
    user: { id: string; name: string },
    field: string,
    values: {
      oldValue?: string | null;
      newValue?: string | null;
      oldDisplay?: string | null;
      newDisplay?: string | null;
    } = {},
  ) => ({
    id: `act-${taskId}-${n}`,
    taskId,
    userId: user.id,
    user: { id: user.id, name: user.name },
    field,
    oldValue: values.oldValue ?? null,
    newValue: values.newValue ?? null,
    oldDisplay: values.oldDisplay ?? null,
    newDisplay: values.newDisplay ?? null,
    createdAt: at,
  });

  const activity = [
    row(1, isoDateTime(-10, 9), reporter, 'created'),
    row(2, isoDateTime(-10, 9, 30), reporter, 'assigneeId', {
      newValue: assignee.id,
      newDisplay: assignee.name,
    }),
    row(3, isoDateTime(-9, 11), reporter, 'priority', { oldValue: 'LOW', newValue: 'MEDIUM' }),
    row(4, isoDateTime(-9, 11, 10), reporter, 'estimate', { newValue: '2' }),
    row(5, isoDateTime(-8, 16), reporter, 'label_added', { newDisplay: 'feature' }),
    row(6, isoDateTime(-5, 10), assignee, 'status', {
      oldDisplay: 'Backlog',
      newDisplay: 'În lucru',
    }),
    // The comment section above shows this one in full, so the history leaves
    // it out — exactly the case the split exists for.
    row(7, isoDateTime(-3, 14), assignee, 'comment_added', {
      newDisplay:
        'Progres bun — am implementat funcționalitatea de bază. Rămâne de integrat cu modulul existent.',
    }),
    row(8, isoDateTime(-2, 12), assignee, 'time_logged', { newValue: '3.5' }),
    row(9, isoDateTime(-2, 12, 30), assignee, 'dueDate', { newValue: isoDate(6) }),
    row(10, isoDateTime(-1, 15), assignee, 'status', {
      oldDisplay: 'În lucru',
      newDisplay: 'În review',
    }),
  ];
  return { activity };
}

/** Comments + change history merged chronologically, as GET /timeline returns. */
export function buildTaskTimeline(taskId: string) {
  const { comments } = buildTaskComments(taskId);
  const { activity } = buildTaskActivity(taskId);
  const timeline = [
    ...comments.map((c) => ({ kind: 'comment' as const, at: c.createdAt, data: c })),
    ...activity.map((a) => ({ kind: 'activity' as const, at: a.createdAt, data: a })),
  ].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
  return { timeline };
}

export function buildTaskSubtasks(taskId: string) {
  const tasks = [
    {
      id: `sub-${taskId}-1`,
      taskId,
      title: 'Analiză cerințe și design tehnic',
      status: 'done',
      statusCategory: 'COMPLETED',
      priority: 'MEDIUM' as const,
      assignee: null,
    },
    {
      id: `sub-${taskId}-2`,
      taskId,
      title: 'Implementare funcționalitate',
      status: 'in_progress',
      statusCategory: 'STARTED',
      priority: 'HIGH' as const,
      assignee: null,
    },
    {
      id: `sub-${taskId}-3`,
      taskId,
      title: 'Teste și documentație',
      status: 'todo',
      statusCategory: 'UNSTARTED',
      priority: 'LOW' as const,
      assignee: null,
    },
  ];
  const done = tasks.filter((t) => t.statusCategory === 'COMPLETED').length;
  return { tasks, progress: { done, total: tasks.length } };
}
